import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import classnames from 'classnames';
import { useAccount, State, Selectors } from '@grandvisionhq/auth';
import { MediaQuery, Button, Modal, Alert } from '@grandvisionhq/common';
import { Container, Text } from '@grandvisionhq/elements';
import { Util } from '@grandvisionhq/forms';
import { useIntl, useSelector } from '@grandvisionhq/state';
import { parseHtml } from '@grandvisionhq/utils';
import { Newsletter } from './Newsletter.js';

const NewsletterContainer = ({ alignment ='left' , className , contentAfterSubmit , contentBeforeSubmit , hasAuthorizationFailure , hasError , hasSubscribed , image , isLoading , mobileImage , modalButtonTitle , title , variant , onSubmit ,  })=>{
    const { getLabel , getFormattedLabel  } = useIntl();
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const [isModalOpen, setIsModalOpen] = React__default.useState(false);
    const [isSubscribeWithEmailChecked, setIsSubscribeWithEmailChecked] = React__default.useState(false);
    const [showSubscribeWithEmailError, setShowSubscribeWithEmailError] = React__default.useState(false);
    const authState = useAccount();
    const isLoggedIn = authState === State.AuthState.LoggedIn;
    const auth = useSelector(Selectors.getAuth);
    const authEmail = isLoggedIn && auth.email ? auth.email : undefined;
    // Make sure we have the necessary content props
    if (!title || !contentBeforeSubmit) {
        return null;
    }
    // Make sure all variants have the required data to render
    const isModal = !!(variant === 'modal' && modalButtonTitle);
    const isBloomreachComponent = !!(variant === 'bloomreach-component' && image);
    if (!isModal && !isBloomreachComponent) {
        return null;
    }
    const imageUrl = isSmallScreen && mobileImage ? mobileImage.url : image === null || image === void 0 ? void 0 : image.url;
    const labels = {
        closeModal: getLabel('common.actions.close'),
        emailAddress: getLabel('fields.input.email.label'),
        emailAddressErrorMessage: getLabel('account.error.emailInvalid'),
        subscribeWithEmail: getFormattedLabel('common.newsletterSignUp.subscribeWithEmail', {
            labelData: {
                email: authEmail !== null && authEmail !== void 0 ? authEmail : ''
            },
            parseLink: true
        }),
        subscribeWithEmailError: getLabel('common.newsletterSignUp.subscribeWithEmailError'),
        generalErrorTitle: getLabel('common.newsletterSignUp.error.generalTitle'),
        generalErrorAlertTitle: getLabel('common.newsletterSignUp.error.generalAlertTitle'),
        generalErrorAlertMessage: getLabel('common.newsletterSignUp.error.generalAlertMessage'),
        incorrectEmailAlertMessage: getLabel('common.newsletterSignUp.error.incorrectEmailAddress'),
        successTitle: getLabel('common.newsletterSignUp.success.title'),
        successAlertTitle: getLabel('common.newsletterSignUp.success.alertTitle'),
        successAlertMessage: getLabel('common.newsletterSignUp.success.alertMessage'),
        signUp: getLabel('common.newsletterSignUp.signUp')
    };
    const handleSubmit = (formState)=>{
        const { email  } = Util.getValuesFromState(formState.formState);
        if (isLoggedIn) {
            setShowSubscribeWithEmailError(false);
            if (!isSubscribeWithEmailChecked) {
                setShowSubscribeWithEmailError(true);
                return;
            }
        }
        if (email) {
            onSubmit({
                email
            });
        }
    };
    const getTitle = ()=>{
        if (hasSubscribed) {
            return labels.successTitle;
        }
        if (hasError) {
            return labels.generalErrorTitle;
        }
        return title;
    };
    const getContent = ()=>{
        // When the user hasn't yet subscribed we always show the `contentBeforeSubmit`.
        if (!hasSubscribed) {
            return /*#__PURE__*/ React__default.createElement(Text, null, parseHtml(contentBeforeSubmit));
        }
        // When the user has successfully subscribed we need to render the `contentAfterSubmit` instead of the `contentBeforeSubmit`.
        if (contentAfterSubmit) {
            return /*#__PURE__*/ React__default.createElement(Text, null, parseHtml(contentAfterSubmit));
        }
        // `contentAfterSubmit` is an optional field in BR, so don't render any content when it's undefined
        return null;
    };
    const getAlert = ()=>{
        if (hasSubscribed) {
            return /*#__PURE__*/ React__default.createElement(Alert, {
                icon: "checkmark-circle",
                message: labels.successAlertMessage,
                title: labels.successAlertTitle,
                type: "success"
            });
        }
        if (hasAuthorizationFailure) {
            return /*#__PURE__*/ React__default.createElement(Alert, {
                message: labels.incorrectEmailAlertMessage,
                icon: "alert-circle",
                type: "error"
            });
        }
        if (hasError) {
            return /*#__PURE__*/ React__default.createElement(Alert, {
                icon: "alert-circle",
                message: labels.generalErrorAlertMessage,
                title: labels.generalErrorAlertTitle,
                type: "error"
            });
        }
        return null;
    };
    const onModalOpen = ()=>{
        setIsModalOpen(true);
    };
    const onModalClose = ()=>{
        setIsModalOpen(false);
    };
    const onSubscribeWithEmailChange = ()=>{
        setIsSubscribeWithEmailChecked(!isSubscribeWithEmailChecked);
    };
    const sharedProps = {
        alert: getAlert(),
        content: getContent(),
        hideForm: hasSubscribed,
        isLoading,
        labels,
        variant,
        authEmail,
        isSubscribeWithEmailChecked,
        showSubscribeWithEmailError,
        onSubscribeWithEmailChange,
        onSubmit: handleSubmit
    };
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('newsletter', className)
    }, isModal ? /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Button, {
        className: "newsletter__toggle",
        caption: modalButtonTitle,
        onClick: onModalOpen
    }), /*#__PURE__*/ React__default.createElement(Modal, {
        className: "newsletter__modal",
        isOpen: isModalOpen,
        onModalClose: onModalClose,
        showIcon: true,
        title: getTitle()
    }, /*#__PURE__*/ React__default.createElement(Newsletter, swcHelpers.extends({}, sharedProps, {
        showCancelButton: true,
        onModalClose: onModalClose
    })))) : null, isBloomreachComponent ? /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('newsletter__section', `newsletter__section--align-${alignment}`),
        "data-background-image": true // For testing purposes
        ,
        style: {
            backgroundImage: `url(${imageUrl})`
        }
    }, /*#__PURE__*/ React__default.createElement(Container, null, /*#__PURE__*/ React__default.createElement(Newsletter, swcHelpers.extends({}, sharedProps, {
        title: getTitle()
    })))) : null);
};
var NewsletterContainer$1 = NewsletterContainer;

export { NewsletterContainer$1 as default };
//# sourceMappingURL=NewsletterContainer.js.map
